<template>
    <div id="app">
        <div class="popover-backdrop" v-if="resourcesOpen" v-teleport @click="resourcesOpen = false"></div>
        <div class="header-toolbar">
            <div class="header-content">
                <div class="logo-section">
                    <div class="logo-icon"><span>🚚</span></div>
                    <h1 class="dashboard-title">Quick SARS</h1>
                </div>
                <div class="header-controls">
                    <div class="resources-badge" @click="resourcesOpen = !resourcesOpen">
                        <span>ℹ️</span>
                        <span>Resources</span>
                        <span class="date-range-chevron" :class="{ open: resourcesOpen }">▾</span>

                        <div class="resources-popover" v-if="resourcesOpen" @click.stop>
                            <a class="resources-link" href="/docs/FAMS-SARS-Diesel-Rebate-Solution.pdf" target="_blank" rel="noopener">
                                <span>📄</span> FAMS SARS Diesel Rebate Solution
                            </a>
                            <a class="resources-link" href="/docs/FAMS-SARS-One-Pager.pdf" target="_blank" rel="noopener">
                                <span>📄</span> FAMS SARS One-Pager
                            </a>
                            <a class="resources-link" href="https://www.sars.gov.za/customs-and-excise/excise/diesel-refund-system/" target="_blank" rel="noopener">
                                <span>🔗</span> SARS Diesel Refund System
                            </a>
                        </div>
                    </div>
                    <div class="live-indicator">
                        <div class="live-dot"></div>
                        <span style="color:white;font-size:0.875rem;">Live</span>
                    </div>
                </div>
            </div>
        </div>

        <div class="main-content">

            <template v-if="rows.length">
                <!-- This period vs previous period -->
                <div class="stats-section" v-if="previousLoaded">
                    <div class="stats-header">
                        <h3 class="stats-title">This Period vs Previous</h3>
                        <div class="stats-subtitle">{{ currentPeriodLabel }} vs {{ previousPeriodLabel }}</div>
                    </div>
                    <div class="stats-wrapper">
                        <div class="stats-container">
                            <div class="stat-card total-stat stat-card-clickable" @click="openTotalsBreakdown('litres')" title="Click to view all equipment, highest litres first">
                                <div class="stat-label">Total Litres Dispensed</div>
                                <div class="stat-value">{{ formatLitres(totals.totalLitres) }}</div>
                                <div class="stat-change" :class="litresChangeClass">{{ litresChangeLabel }}</div>
                            </div>
                            <div class="stat-card average-stat stat-card-clickable" @click="openTotalsBreakdown('sars')" title="Click to view all equipment, highest SARS claim first">
                                <div class="stat-label">Total SARS Claim</div>
                                <div class="stat-value">{{ formatCurrency(totals.totalSars) }}</div>
                                <div class="stat-change" :class="sarsChangeClass">{{ sarsChangeLabel }}</div>
                            </div>
                        </div>
                        <div class="period-comparison-chart">
                            <div class="trend-metric-picker">
                                <span class="trend-metric-picker-label">Show on chart:</span>
                                <button v-for="m in trendMetricOptions" :key="m.key"
                                    :class="['chip-btn', { active: selectedTrendMetrics.indexOf(m.key) !== -1 }]"
                                    :style="selectedTrendMetrics.indexOf(m.key) !== -1 ? { background: m.color, borderColor: m.color } : {}"
                                    @click="toggleTrendMetric(m.key)">
                                    {{ m.label }}
                                </button>
                            </div>
                            <div v-if="trendLoading" class="loading-overlay-inline"><div class="loader"></div></div>
                            <vue-apex-charts v-else type="line" height="300" :options="periodComparisonChartOptions" :series="periodComparisonChartSeries" />
                            <div class="comparison-summary">
                                <div class="comparison-summary-item">
                                    <span class="comparison-summary-label">Transactions</span>
                                    <span class="comparison-summary-value">{{ previousTotals.totalTransactions }} → {{ totals.totalTransactions }}</span>
                                    <span class="comparison-summary-change" :class="transactionsChangeClass">{{ transactionsChangeLabel }}</span>
                                </div>
                                <div class="comparison-summary-item">
                                    <span class="comparison-summary-label">Equipment Reporting</span>
                                    <span class="comparison-summary-value">{{ previousTotals.equipmentCount }} → {{ totals.equipmentCount }}</span>
                                    <span class="comparison-summary-change" :class="equipmentChangeClass">{{ equipmentChangeLabel }}</span>
                                </div>
                                <div class="comparison-summary-item">
                                    <span class="comparison-summary-label">Avg SARS / Transaction</span>
                                    <span class="comparison-summary-value">{{ formatCurrency(avgSarsPerTransactionPrevious) }} → {{ formatCurrency(avgSarsPerTransaction) }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <!-- Filters -->
            <div class="search-section">
                <div class="filter-header">
                    <h3 class="filter-title">Report Selection</h3>
                    <div class="filter-header-buttons">
                        <button class="toggle-filter-btn" @click="filtersVisible = !filtersVisible">
                            <span>{{ filtersVisible ? '📁' : '📂' }}</span> {{ filtersVisible ? 'Hide Filters' : 'Show Filters' }}
                        </button>
                        <button class="reset-filter-header-btn" @click="resetFilters">
                            <span class="reset-icon">↻</span> Reset Filters
                        </button>
                    </div>
                </div>
                <transition name="filter-collapse">
                <div class="search-controls-wrapper" v-show="filtersVisible">
                    <div class="date-quick-buttons">
                        <button v-for="mode in filterModes" :key="mode.value"
                            :class="['date-quick-btn', { active: filterMode === mode.value }]"
                            @click="filterMode = mode.value">
                            {{ mode.label }}
                        </button>
                    </div>
                    <div class="search-controls" style="margin-top:12px;" v-if="filterMode === 'month'">
                        <select v-model.number="params.year" class="specific-date-input specific-date-input-sm">
                            <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
                        </select>
                        <select v-model.number="params.month" class="specific-date-input specific-date-input-sm">
                            <option v-for="m in monthOptions" :key="m.value" :value="m.value">{{ m.label }}</option>
                        </select>
                    </div>
                    <div class="search-controls" style="margin-top:12px;" v-else-if="filterMode === 'year'">
                        <select v-model.number="params.year" class="specific-date-input specific-date-input-sm">
                            <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
                        </select>
                    </div>
                    <!-- From/To are always selectable, regardless of quick mode - picking either switches to Date Range -->
                    <div class="search-controls" style="margin-top:12px;">
                        <div class="search-group">
                            <label class="search-label" style="display:block;font-size:.75rem;color:#64748b;margin-bottom:2px;">From Date</label>
                            <input type="date" v-model="params.fromDate" @change="filterMode = 'range'" class="specific-date-input specific-date-input-sm" />
                        </div>
                        <div class="search-group">
                            <label class="search-label" style="display:block;font-size:.75rem;color:#64748b;margin-bottom:2px;">To Date</label>
                            <input type="date" v-model="params.toDate" @change="filterMode = 'range'" class="specific-date-input specific-date-input-sm" />
                        </div>
                        <div class="search-group">
                            <label class="search-label" style="display:block;font-size:.75rem;color:#64748b;margin-bottom:2px;">Search</label>
                            <input type="text" v-model="quickSearch" placeholder="Search equipment…" class="specific-date-input specific-date-input-sm" />
                        </div>
                        <div class="search-group">
                            <label class="search-label" style="display:block;font-size:.75rem;color:#64748b;margin-bottom:2px;">Eligibility</label>
                            <select v-model="statusFilter" class="specific-date-input specific-date-input-sm">
                                <option value="">All Status</option>
                                <option value="Yes">Eligible</option>
                                <option value="No">Non-Eligible</option>
                            </select>
                        </div>
                        <div class="search-group">
                            <label class="search-label" style="display:block;font-size:.75rem;color:#64748b;margin-bottom:2px;">Master Group</label>
                            <select v-model="masterGroupFilter" class="specific-date-input specific-date-input-sm">
                                <option value="">All Master Groups</option>
                                <option v-for="g in masterGroupOptions" :key="g" :value="g">{{ g }}</option>
                            </select>
                        </div>
                        <div class="search-group">
                            <label class="search-label" style="display:block;font-size:.75rem;color:#64748b;margin-bottom:2px;">Cost Centre</label>
                            <select v-model="costCentreFilter" class="specific-date-input specific-date-input-sm">
                                <option value="">All Cost Centres</option>
                                <option v-for="c in costCentreOptions" :key="c" :value="c">{{ c }}</option>
                            </select>
                        </div>
                        <div class="search-group">
                            <label class="search-label" style="display:block;font-size:.75rem;color:#64748b;margin-bottom:2px;" title="Fuel Levy + RAF Levy, at 100% of eligible consumption effective 1 April 2026. Confirm against the current SARS Schedule 6 rate before relying on these figures.">Rebate Rate (R / Litre) ⓘ</label>
                            <input type="number" step="0.001" min="0" v-model.number="rebateRatePerLitre" @change="onRebateRateChange" class="specific-date-input specific-date-input-sm" />
                        </div>
                    </div>
                    <div style="margin-top:12px; display:flex; gap:10px;">
                        <button class="toggle-filter-btn" :disabled="loading" @click="loadData">🔍 Load Report</button>
                        <button class="toggle-filter-btn" style="background:linear-gradient(135deg,#27AE60 0%,#2ECC71 100%);" :disabled="!filteredRows.length" @click="exportToExcel">📊 Export to Excel</button>
                    </div>
                </div>
                </transition>
            </div>

            <div v-if="loading" class="loading-overlay-inline"><div class="loader"></div></div>

            <template v-else-if="rows.length">
                <!-- Equipment filter tabs -->
                <div class="search-section">
                    <div class="filter-header">
                        <h3 class="filter-title">Filter Equipment</h3>
                    </div>
                    <div class="search-controls-wrapper">
                        <div class="date-quick-buttons">
                            <button :class="['date-quick-btn', { active: filterTab === 'all' }]" @click="selectFilterTab('all')">All Equipment</button>
                            <button :class="['date-quick-btn', { active: filterTab === 'masterGroup' }]" @click="selectFilterTab('masterGroup')">Master Group</button>
                            <button :class="['date-quick-btn', { active: filterTab === 'costCentre' }]" @click="selectFilterTab('costCentre')">Cost Centre</button>
                            <button :class="['date-quick-btn', { active: filterTab === 'product' }]" @click="selectFilterTab('product')">Product</button>
                        </div>
                        <div class="chip-row" v-if="filterTab === 'masterGroup'" style="margin-top:12px;">
                            <button :class="['chip-btn', { active: !masterGroupFilter }]" @click="masterGroupFilter = ''">All</button>
                            <button v-for="g in masterGroupOptions" :key="g" :class="['chip-btn', { active: masterGroupFilter === g }]" @click="masterGroupFilter = g">{{ g }}</button>
                        </div>
                        <div class="chip-row" v-if="filterTab === 'costCentre'" style="margin-top:12px;">
                            <button :class="['chip-btn', { active: !costCentreFilter }]" @click="costCentreFilter = ''">All</button>
                            <button v-for="c in costCentreOptions" :key="c" :class="['chip-btn', { active: costCentreFilter === c }]" @click="costCentreFilter = c">{{ c }}</button>
                        </div>
                    </div>
                </div>

                <!-- Usage breakdown - click a group to reveal the equipment under it -->
                <div class="search-section" v-if="filterTab !== 'all'">
                    <div class="filter-header"><h3 class="filter-title">Usage Breakdown — {{ usageBreakdownTitle }}</h3></div>
                    <div v-if="!usageBreakdownGrouped.length" class="empty-state">
                        <div class="empty-icon">📭</div>
                        <div class="empty-title">No Usage Data</div>
                        <div class="empty-description">No usage found for this period.</div>
                    </div>
                    <div v-else class="usage-group-list">
                        <div class="usage-group" v-for="g in usageBreakdownGrouped" :key="g.name">
                            <button class="usage-group-toggle" @click="toggleUsageGroup(g.name)">
                                <span class="usage-group-arrow" :class="{ open: expandedUsageGroup === g.name }">▸</span>
                                <span class="usage-group-name">{{ g.name }}</span>
                                <span class="usage-group-meta">{{ g.equipmentRows.length }} equipment · {{ formatLitres(g.totalLitres) }} · {{ formatCurrency(g.totalSars) }} · {{ g.totalTransactions }} transactions</span>
                            </button>
                            <div class="usage-group-body" v-if="expandedUsageGroup === g.name">
                                <div class="card-grid">
                                    <div class="equipment-card" v-for="row in g.equipmentRows" :key="row.equipmentID">
                                        <button class="card-eye-btn" title="View full last transaction" @click.stop="viewFullTransactionForRow(row)">👁️</button>
                                        <div class="card-top">
                                            <span class="equipment-name">{{ row.equipment }}</span>
                                            <span :class="row.sarsEligible === 'Yes' ? 'badge-eligible' : 'badge-non-eligible'">
                                                {{ row.sarsEligible === 'Yes' ? 'Eligible' : 'Non-Eligible' }}
                                            </span>
                                        </div>
                                        <div class="equipment-reg" v-if="row.equipmentRegistration" :title="row.equipmentRegistration">{{ row.equipmentRegistration }}</div>
                                        <div class="card-stats">
                                            <div class="card-stat">
                                                <div class="card-stat-label">Litres</div>
                                                <div class="card-stat-value">{{ formatLitres(row.totalLitresDispensed) }}</div>
                                            </div>
                                            <div class="card-stat">
                                                <div class="card-stat-label">SARS Amount</div>
                                                <div class="card-stat-value">{{ formatCurrency(computedSarsAmount(row)) }}</div>
                                            </div>
                                            <div class="card-stat">
                                                <div class="card-stat-label">Transactions</div>
                                                <div class="card-stat-value">{{ row.transactionCount }}</div>
                                            </div>
                                            <div class="card-stat">
                                                <div class="card-stat-label">Status</div>
                                                <div class="card-stat-value" :class="complianceClass(row.complianceStatus)">{{ row.complianceStatus }}</div>
                                            </div>
                                        </div>
                                        <div class="card-footer">
                                            <span>Last activity: {{ formatDate(row.lastDispensingDate) }}</span>
                                            <div class="card-footer-actions">
                                                <button class="view-transactions-btn" @click.stop="openTransactionList(row)">📋 View Transactions</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="card-grid" v-if="filterTab === 'all' && filteredRows.length">
                    <div class="equipment-card" v-for="row in filteredRows" :key="row.equipmentID">
                        <button class="card-eye-btn" title="View full last transaction" @click.stop="viewFullTransactionForRow(row)">👁️</button>
                        <div class="card-top">
                            <span class="equipment-name" :title="row.equipment">{{ row.equipment }}</span>
                            <span class="equipment-badge" :class="row.sarsEligible === 'Yes' ? 'badge-eligible' : 'badge-non-eligible'">
                                {{ row.sarsEligible === 'Yes' ? 'Eligible' : 'Non-Eligible' }}
                            </span>
                        </div>
                        <div class="equipment-reg" v-if="row.equipmentRegistration" :title="row.equipmentRegistration">{{ row.equipmentRegistration }}</div>
                        <div class="card-stats">
                            <div class="card-stat">
                                <div class="card-stat-label">Litres</div>
                                <div class="card-stat-value">{{ formatLitres(row.totalLitresDispensed) }}</div>
                            </div>
                            <div class="card-stat">
                                <div class="card-stat-label">SARS Amount</div>
                                <div class="card-stat-value">{{ formatCurrency(computedSarsAmount(row)) }}</div>
                            </div>
                            <div class="card-stat">
                                <div class="card-stat-label">Transactions</div>
                                <div class="card-stat-value">{{ row.transactionCount }}</div>
                            </div>
                            <div class="card-stat">
                                <div class="card-stat-label">Status</div>
                                <div class="card-stat-value" :class="complianceClass(row.complianceStatus)">{{ row.complianceStatus }}</div>
                            </div>
                        </div>
                        <div class="card-footer">
                            <span>Last activity: {{ formatDate(row.lastDispensingDate) }}</span>
                            <div class="card-footer-actions">
                                <button class="view-transactions-btn" @click.stop="openTransactionList(row)">📋 View Transactions</button>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-else-if="filterTab === 'all'" class="empty-state">
                    <div class="empty-icon">🔍</div>
                    <div class="empty-title">No Matching Equipment</div>
                    <div class="empty-description">No equipment matches the current search/status filter.</div>
                </div>
            </template>

            <div v-else class="empty-state">
                <div class="empty-icon">📭</div>
                <div class="empty-title">No Data Loaded</div>
                <div class="empty-description">Select a period and click <strong>Load Report</strong>.</div>
            </div>
        </div>

        <!-- Drill-down popup -->
        <div v-if="selectedEquipment" class="simple-popup" v-teleport @click.self="closeDrillDown">
            <div class="popup-content">
                <div class="popup-header">
                    <h3>🚚 {{ selectedEquipment.equipment }}</h3>
                    <button class="close-btn" @click="closeDrillDown">×</button>
                </div>
                <div class="popup-body">
                    <div class="detail-grid">
                        <div class="detail-row"><strong>Registration:</strong> {{ selectedEquipment.equipmentRegistration || 'N/A' }}</div>
                        <div class="detail-row"><strong>Status:</strong> {{ selectedEquipment.sarsEligible === 'Yes' ? 'Eligible' : 'Non-Eligible' }}</div>
                        <div class="detail-row"><strong>Litres (Period):</strong> {{ formatLitres(selectedEquipment.totalLitresDispensed) }}</div>
                        <div class="detail-row"><strong>SARS Amount:</strong> {{ formatCurrency(computedSarsAmount(selectedEquipment)) }}</div>
                        <div class="detail-row"><strong>Transactions:</strong> {{ selectedEquipment.transactionCount }}</div>
                        <div class="detail-row"><strong>Compliance:</strong> {{ selectedEquipment.complianceStatus }}</div>
                    </div>

                    <h4 class="popup-subtitle">Last Dispensing</h4>
                    <div class="detail-grid">
                        <div class="detail-row"><strong>Date &amp; Time:</strong> {{ formatDateTime(selectedEquipment.lastDispensingDate) }}</div>
                        <div class="detail-row"><strong>Volume:</strong> {{ formatLitres(selectedEquipment.lastDispensingVolumeLitres) }}</div>
                        <div class="detail-row"><strong>Location:</strong> {{ selectedEquipment.lastDispensingLocation || 'N/A' }}</div>
                        <div class="detail-row"><strong>Operator:</strong> {{ selectedEquipment.lastDispensingOperation || 'N/A' }}</div>
                        <div class="detail-row"><strong>Product:</strong> {{ selectedEquipment.lastProductDispensed || 'N/A' }}</div>
                        <div class="detail-row" v-if="selectedEquipment.lastDispensingKM"><strong>KM Reading:</strong> {{ selectedEquipment.lastDispensingKM }} km</div>
                        <div class="detail-row" v-if="selectedEquipment.lastDispensingHour"><strong>Hours Meter:</strong> {{ selectedEquipment.lastDispensingHour }} hrs</div>
                    </div>

                    <div v-if="transactionLoading" class="loading-overlay-inline" style="padding:20px 0;"><div class="loader"></div></div>

                    <div v-if="transactionDetail" class="transaction-detail">
                        <h4 class="popup-subtitle">Full Transaction — Last Activity on this Equipment</h4>
                        <div class="txn-detail-card">
                            <div class="txn-detail-row txn-detail-highlight">
                                <span class="txn-detail-label">Date &amp; Time</span>
                                <span class="txn-detail-value">{{ formatDateTime(transactionDetail.createdate) }}</span>
                            </div>
                            <div class="txn-detail-grid">
                                <div class="txn-detail-cell">
                                    <span class="txn-detail-label">Volume</span>
                                    <span class="txn-detail-value">{{ formatLitres(transactionDetail.volume) }}</span>
                                </div>
                                <div class="txn-detail-cell" v-if="transactionDetail.km">
                                    <span class="txn-detail-label">KM Reading</span>
                                    <span class="txn-detail-value">{{ transactionDetail.km }} km</span>
                                </div>
                                <div class="txn-detail-cell" v-if="transactionDetail.hour">
                                    <span class="txn-detail-label">Hours Meter</span>
                                    <span class="txn-detail-value">{{ transactionDetail.hour }} hrs</span>
                                </div>
                                <div class="txn-detail-cell">
                                    <span class="txn-detail-label">Store</span>
                                    <span class="txn-detail-value">{{ transactionDetail.store || 'N/A' }}</span>
                                </div>
                                <div class="txn-detail-cell">
                                    <span class="txn-detail-label">Operator</span>
                                    <span class="txn-detail-value">{{ transactionDetail.operator || 'N/A' }}</span>
                                </div>
                                <div class="txn-detail-cell">
                                    <span class="txn-detail-label">Product</span>
                                    <span class="txn-detail-value">{{ transactionDetail.product || 'N/A' }}</span>
                                </div>
                                <div class="txn-detail-cell" v-if="transactionDetail.jobNumber">
                                    <span class="txn-detail-label">Job Number</span>
                                    <span class="txn-detail-value">{{ transactionDetail.jobNumber }}</span>
                                </div>
                                <div class="txn-detail-cell" v-if="transactionDetail.totalizer != null">
                                    <span class="txn-detail-label">Totalizer Start</span>
                                    <span class="txn-detail-value">{{ transactionDetail.totalizer }}</span>
                                </div>
                                <div class="txn-detail-cell" v-if="transactionDetail.totalizerEnd != null">
                                    <span class="txn-detail-label">Totalizer End</span>
                                    <span class="txn-detail-value">{{ transactionDetail.totalizerEnd }}</span>
                                </div>
                                <div class="txn-detail-cell">
                                    <span class="txn-detail-label">Account</span>
                                    <span class="txn-detail-value">{{ transactionDetail.account || 'N/A' }}</span>
                                </div>
                            </div>
                            <div class="txn-detail-description" v-if="transactionDetail.description">
                                <span class="txn-detail-label">Description</span>
                                <span class="txn-detail-value">{{ transactionDetail.description }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Full transaction list popup -->
        <div v-if="transactionListEquipment" class="simple-popup" v-teleport @click.self="closeTransactionList">
            <div class="popup-content" style="max-width:800px;">
                <div class="popup-header">
                    <h3>📋 {{ transactionListEquipment.equipment }} — All Transactions</h3>
                    <button class="close-btn" @click="closeTransactionList">×</button>
                </div>
                <div class="popup-body">
                    <div v-if="transactionListLoading" class="loading-overlay-inline"><div class="loader"></div></div>
                    <div v-else-if="!transactionList.length" class="empty-state">
                        <div class="empty-icon">📭</div>
                        <div class="empty-title">No Transactions</div>
                        <div class="empty-description">No transactions found for this equipment in the selected period.</div>
                    </div>
                    <div v-else class="transaction-list-wrapper">
                        <table class="transaction-list-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Store</th>
                                    <th>Operator</th>
                                    <th>Product</th>
                                    <th>Volume</th>
                                    <th>KM</th>
                                    <th>Hour</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="t in transactionList" :key="t.id">
                                    <td>{{ formatDateTime(t.createdate) }}</td>
                                    <td>{{ t.store || 'N/A' }}</td>
                                    <td>{{ t.operator || 'N/A' }}</td>
                                    <td>{{ t.product || 'N/A' }}</td>
                                    <td>{{ formatLitres(t.volume) }}</td>
                                    <td>{{ t.km || '-' }}</td>
                                    <td>{{ t.hour || '-' }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <!-- Totals breakdown popup - all equipment for this period, ranked
             high to low by whichever total stat card was clicked -->
        <div v-if="totalsBreakdownOpen" class="simple-popup" v-teleport @click.self="closeTotalsBreakdown">
            <div class="popup-content" style="max-width:800px;">
                <div class="popup-header">
                    <h3>📊 {{ totalsBreakdownOpen === 'litres' ? 'Total Litres Dispensed' : 'Total SARS Claim' }} — By Equipment</h3>
                    <button class="close-btn" @click="closeTotalsBreakdown">×</button>
                </div>
                <div class="popup-body">
                    <div v-if="!totalsBreakdownRows.length" class="empty-state">
                        <div class="empty-icon">📭</div>
                        <div class="empty-title">No Data</div>
                        <div class="empty-description">No equipment found for this period.</div>
                    </div>
                    <div v-else class="transaction-list-wrapper">
                        <table class="transaction-list-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Equipment</th>
                                    <th>Registration</th>
                                    <th>{{ totalsBreakdownOpen === 'litres' ? 'Litres' : 'SARS Amount' }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(row, idx) in totalsBreakdownRows" :key="row.equipmentID" @click="closeTotalsBreakdown(); viewFullTransactionForRow(row);" style="cursor:pointer;">
                                    <td>{{ idx + 1 }}</td>
                                    <td>{{ row.equipment }}</td>
                                    <td>{{ row.equipmentRegistration || 'N/A' }}</td>
                                    <td>{{ totalsBreakdownOpen === 'litres' ? formatLitres(row.totalLitresDispensed) : formatCurrency(computedSarsAmount(row)) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import * as XLSX from 'xlsx';
import VueApexCharts from 'vue-apexcharts';

export default {
    name: 'SARSTracker',
    components: { VueApexCharts },
    // The admin layout's route-transition wrapper applies a CSS transform,
    // which turns it into the containing block for any position:fixed
    // descendant - so full-screen overlays (popups, backdrops) end up
    // sized/positioned relative to that wrapper instead of the real
    // viewport, clipping and misaligning them. Moving the element straight
    // onto <body> on insert sidesteps that entirely; Vue still removes it
    // correctly later since removeChild is called on el.parentNode.
    directives: {
        teleport: {
            inserted: function (el) { document.body.appendChild(el); }
        }
    },

    data: function () {
        var today = new Date();
        var savedRate = parseFloat(window.localStorage ? localStorage.getItem('sarsRebateRatePerLitre') : null);
        return {
            loading: false,
            transactionLoading: false,
            rows: [],
            previousRows: [],
            previousLoaded: false,
            quickSearch: '',
            statusFilter: '',
            filterTab: 'all',
            masterGroupFilter: '',
            costCentreFilter: '',
            filtersVisible: true,
            selectedEquipment: null,
            transactionDetail: null,
            transactionListEquipment: null,
            transactionList: [],
            transactionListLoading: false,
            expandedUsageGroup: null,
            totalsBreakdownOpen: null,
            resourcesOpen: false,
            trendData: [],
            trendLoading: false,
            selectedTrendMetrics: ['totalLitres', 'totalSars'],
            trendMetricOptions: [
                {
                    key: 'totalLitres', label: 'Litres Dispensed', color: '#D2691E', markerColor: '#FFC107',
                    format: function (val) { return Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 }); },
                    tooltipFormat: function (val) { return Number(val).toLocaleString(undefined, { maximumFractionDigits: 2 }) + ' L'; }
                },
                {
                    key: 'totalSars', label: 'SARS Claim', color: '#27AE60', markerColor: '#8BE0B0',
                    format: function (val) { return 'R ' + Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 }); },
                    tooltipFormat: function (val) { return 'R ' + Number(val).toLocaleString(undefined, { maximumFractionDigits: 2 }); }
                },
                {
                    key: 'totalTransactions', label: 'Transactions', color: '#2196F3', markerColor: '#90CAF9',
                    format: function (val) { return Number(val).toLocaleString(); },
                    tooltipFormat: function (val) { return Number(val).toLocaleString() + ' transactions'; }
                },
                {
                    key: 'equipmentCount', label: 'Equipment Reporting', color: '#9C27B0', markerColor: '#CE93D8',
                    format: function (val) { return Number(val).toLocaleString(); },
                    tooltipFormat: function (val) { return Number(val).toLocaleString() + ' equipment'; }
                }
            ],
            // R3.038/L = Fuel Levy (78.8c/L) + RAF Levy (225.0c/L), the
            // published SARS diesel refund rate effective 1 April 2026 at
            // 100% of eligible consumption (previously 80%). Source: FAMS
            // SARS Diesel Refund Solution one-pager. Always confirm against
            // the current SARS Schedule 6 rate, since it changes whenever
            // the underlying levies are amended.
            rebateRatePerLitre: isNaN(savedRate) ? 3.038 : savedRate,
            filterMode: 'month',
            filterModes: [
                { value: 'month', label: 'Month' },
                { value: 'range', label: 'Date Range' },
                { value: 'year', label: 'Whole Year' }
            ],
            params: { year: today.getFullYear(), month: today.getMonth() + 1, fromDate: '', toDate: '' },
            monthOptions: [
                { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
                { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
                { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
                { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
            ]
        };
    },

    computed: {
        yearOptions: function () {
            var current = new Date().getFullYear();
            var years = [];
            for (var y = current; y >= current - 4; y--) years.push(y);
            return years;
        },

        usageBreakdownTitle: function () {
            if (this.filterTab === 'masterGroup') return 'By Master Equipment Group';
            if (this.filterTab === 'costCentre') return 'By Cost Centre';
            if (this.filterTab === 'product') return 'By Product';
            return '';
        },

        // Groups the FILTERED equipment rows (search/eligibility/master
        // group/cost centre filters, and the tab's own chip selection all
        // apply here too) by dimension - Master Group / Cost Centre / last-
        // dispensed Product - so each group can be shown as a collapsed
        // dropdown that reveals the equipment under it - as cards, matching
        // the rest of the page - only once clicked.
        usageBreakdownGrouped: function () {
            var self = this;
            var field = this.filterTab === 'masterGroup' ? 'masterEquipmentGroup' : this.filterTab === 'costCentre' ? 'costCentre' : 'lastProductDispensed';
            var groups = {};
            var order = [];
            this.filteredRows.forEach(function (row) {
                var name = row[field] || 'Unspecified';
                if (!groups[name]) { groups[name] = { name: name, totalLitres: 0, totalSars: 0, totalTransactions: 0, equipmentRows: [] }; order.push(name); }
                groups[name].totalLitres += Number(row.totalLitresDispensed) || 0;
                groups[name].totalSars += self.computedSarsAmount(row);
                groups[name].totalTransactions += Number(row.transactionCount) || 0;
                groups[name].equipmentRows.push(row);
            });
            return order.map(function (name) { return groups[name]; })
                .sort(function (a, b) { return b.totalSars - a.totalSars; });
        },

        filteredRows: function () {
            var self = this;
            var result = this.rows;
            if (this.statusFilter) {
                result = result.filter(function (r) { return r.sarsEligible === self.statusFilter; });
            }
            if (this.masterGroupFilter) {
                result = result.filter(function (r) { return r.masterEquipmentGroup === self.masterGroupFilter; });
            }
            if (this.costCentreFilter) {
                result = result.filter(function (r) { return r.costCentre === self.costCentreFilter; });
            }
            if (this.quickSearch) {
                var q = this.quickSearch.toLowerCase();
                result = result.filter(function (r) {
                    return (r.equipment && r.equipment.toLowerCase().includes(q)) ||
                        (r.equipmentRegistration && r.equipmentRegistration.toLowerCase().includes(q));
                });
            }
            return result.slice().sort(function (a, b) { return self.computedSarsAmount(b) - self.computedSarsAmount(a); });
        },

        masterGroupOptions: function () {
            var values = this.rows.map(function (r) { return r.masterEquipmentGroup; }).filter(function (v) { return v; });
            return values.filter(function (v, i) { return values.indexOf(v) === i; }).sort();
        },
        costCentreOptions: function () {
            var values = this.rows.map(function (r) { return r.costCentre; }).filter(function (v) { return v; });
            return values.filter(function (v, i) { return values.indexOf(v) === i; }).sort();
        },
        totals: function () {
            return this.periodTotals(this.rows);
        },

        // All equipment for the current period, ranked highest to lowest by
        // whichever total stat card was clicked - matches the aggregate
        // shown in that card since both are derived from the same `rows`.
        totalsBreakdownRows: function () {
            var self = this;
            if (!this.totalsBreakdownOpen) return [];
            return this.rows.slice().sort(function (a, b) {
                if (self.totalsBreakdownOpen === 'sars') return self.computedSarsAmount(b) - self.computedSarsAmount(a);
                return (Number(b.totalLitresDispensed) || 0) - (Number(a.totalLitresDispensed) || 0);
            });
        },

        previousTotals: function () {
            return this.periodTotals(this.previousRows);
        },

        // Human-readable label for whatever period is currently loaded, so
        // "This Period vs Previous" doesn't leave the viewer guessing which
        // actual dates the numbers below cover.
        currentPeriodLabel: function () {
            if (this.filterMode === 'year') return String(this.params.year);
            if (this.filterMode === 'range') {
                if (!this.params.fromDate || !this.params.toDate) return '';
                return this.formatDateShort(this.params.fromDate) + ' – ' + this.formatDateShort(this.params.toDate);
            }
            return this.monthLabel(this.params.month) + ' ' + this.params.year;
        },
        // Mirrors buildPreviousQueryString's own logic so the label always
        // matches whatever period previousRows actually holds.
        previousPeriodLabel: function () {
            if (this.filterMode === 'year') return String(this.params.year - 1);
            if (this.filterMode === 'range') {
                if (!this.params.fromDate || !this.params.toDate) return '';
                var from = new Date(this.params.fromDate);
                var to = new Date(this.params.toDate);
                var days = Math.round((to - from) / 86400000) + 1;
                var prevTo = new Date(from);
                prevTo.setDate(prevTo.getDate() - 1);
                var prevFrom = new Date(prevTo);
                prevFrom.setDate(prevFrom.getDate() - (days - 1));
                return this.formatDateShort(prevFrom) + ' – ' + this.formatDateShort(prevTo);
            }
            var year = this.params.year, month = this.params.month - 1;
            if (month < 1) { month = 12; year -= 1; }
            return this.monthLabel(month) + ' ' + year;
        },

        litresChangePct: function () {
            if (!this.previousTotals.totalLitres) return null;
            return ((this.totals.totalLitres - this.previousTotals.totalLitres) / this.previousTotals.totalLitres) * 100;
        },
        litresChangeLabel: function () {
            if (this.litresChangePct === null) return 'No prior-period data';
            var pct = this.litresChangePct;
            return (pct >= 0 ? '↑ ' : '↓ ') + Math.abs(pct).toFixed(1) + '% vs previous period';
        },
        litresChangeClass: function () {
            if (this.litresChangePct === null) return '';
            return this.litresChangePct >= 0 ? 'positive' : 'negative';
        },

        sarsChangePct: function () {
            if (!this.previousTotals.totalSars) return null;
            return ((this.totals.totalSars - this.previousTotals.totalSars) / this.previousTotals.totalSars) * 100;
        },
        sarsChangeLabel: function () {
            if (this.sarsChangePct === null) return 'No prior-period data';
            var pct = this.sarsChangePct;
            return (pct >= 0 ? '↑ ' : '↓ ') + Math.abs(pct).toFixed(1) + '% vs previous period';
        },
        sarsChangeClass: function () {
            if (this.sarsChangePct === null) return '';
            return this.sarsChangePct >= 0 ? 'positive' : 'negative';
        },

        transactionsChangePct: function () {
            if (!this.previousTotals.totalTransactions) return null;
            return ((this.totals.totalTransactions - this.previousTotals.totalTransactions) / this.previousTotals.totalTransactions) * 100;
        },
        transactionsChangeLabel: function () {
            if (this.transactionsChangePct === null) return 'No prior-period data';
            var pct = this.transactionsChangePct;
            return (pct >= 0 ? '↑ ' : '↓ ') + Math.abs(pct).toFixed(1) + '%';
        },
        transactionsChangeClass: function () {
            if (this.transactionsChangePct === null) return '';
            return this.transactionsChangePct >= 0 ? 'positive' : 'negative';
        },

        equipmentChangePct: function () {
            if (!this.previousTotals.equipmentCount) return null;
            return ((this.totals.equipmentCount - this.previousTotals.equipmentCount) / this.previousTotals.equipmentCount) * 100;
        },
        equipmentChangeLabel: function () {
            if (this.equipmentChangePct === null) return 'No prior-period data';
            var pct = this.equipmentChangePct;
            return (pct >= 0 ? '↑ ' : '↓ ') + Math.abs(pct).toFixed(1) + '%';
        },
        equipmentChangeClass: function () {
            if (this.equipmentChangePct === null) return '';
            return this.equipmentChangePct >= 0 ? 'positive' : 'negative';
        },

        avgSarsPerTransaction: function () {
            return this.totals.totalTransactions ? this.totals.totalSars / this.totals.totalTransactions : 0;
        },
        avgSarsPerTransactionPrevious: function () {
            return this.previousTotals.totalTransactions ? this.previousTotals.totalSars / this.previousTotals.totalTransactions : 0;
        },

        // Only the metrics the user has ticked on, in the fixed order
        // defined by trendMetricOptions (not selection order) so the chart
        // doesn't jump colors/axes around as things are toggled.
        selectedTrendMetricConfigs: function () {
            var self = this;
            return this.trendMetricOptions.filter(function (m) { return self.selectedTrendMetrics.indexOf(m.key) !== -1; });
        },

        // A real trend line - the last 6 months plus "This Period" - rather
        // than just two dots, so the x-axis actually shows a history to
        // read a direction from. Which metrics appear is entirely up to the
        // user (selectedTrendMetrics); series/axes/colors are all derived
        // from that selection.
        periodComparisonChartSeries: function () {
            return this.selectedTrendMetricConfigs.map(function (m) {
                return { name: m.label, data: this.trendData.map(function (t) { return Math.round(Number(t[m.key]) || 0); }) };
            }, this);
        },
        periodComparisonChartOptions: function () {
            var self = this;
            var configs = this.selectedTrendMetricConfigs;
            // The first two selected metrics almost never share a scale
            // (litres vs. rand vs. raw counts), and if two ARE
            // proportional (e.g. Litres and SARS Claim, since SARS =
            // litres x a constant rate) scaling each axis tightly to its
            // own data makes both lines trace the identical normalized
            // path - one ends up drawn 100% underneath the other,
            // invisible. Giving the first two visible axes different
            // headroom multipliers keeps both axes honest (labels still
            // show real values) while pulling the lines apart vertically.
            // Anything beyond the first two gets a hidden axis - its exact
            // value still shows in the tooltip.
            return {
                chart: { toolbar: { show: false }, fontFamily: 'inherit' },
                title: {
                    text: '6-Month Trend',
                    align: 'center',
                    style: { fontSize: '18px', fontWeight: 700, color: '#2c3e50' }
                },
                colors: configs.map(function (m) { return m.color; }),
                stroke: { width: 4, curve: 'straight' },
                markers: {
                    size: 7, strokeWidth: 2, strokeColors: '#fff',
                    colors: configs.map(function (m) { return m.markerColor; }),
                    hover: { size: 9 }
                },
                grid: {
                    borderColor: '#dcdfe3',
                    xaxis: { lines: { show: true } },
                    yaxis: { lines: { show: true } }
                },
                xaxis: {
                    categories: this.trendData.map(function (t) { return t.label; }),
                    labels: { style: { fontSize: '13px', fontWeight: 700, colors: '#2c3e50' } }
                },
                dataLabels: { enabled: false },
                yaxis: configs.map(function (m, idx) {
                    if (idx > 1) return { show: false, min: 0 };
                    return {
                        opposite: idx === 1,
                        min: 0,
                        max: self.trendAxisMax(m.key, idx === 0 ? 1.15 : 1.75),
                        forceNiceScale: true,
                        tickAmount: 5,
                        title: { text: m.label, style: { fontWeight: 700 } },
                        labels: { style: { fontWeight: 600 }, formatter: m.format }
                    };
                }),
                tooltip: {
                    shared: true,
                    x: { show: true },
                    y: configs.map(function (m) { return { formatter: m.tooltipFormat }; })
                },
                // Non-floating - a floating legend sits inside the plot
                // area and collided with the topmost axis labels/data
                // points whenever the lines rode close to the top of the
                // chart. A normal top legend reserves its own row instead.
                legend: {
                    position: 'top', horizontalAlign: 'center',
                    fontSize: '13px', fontWeight: 600,
                    markers: { width: 14, height: 14, radius: 3 },
                    itemMargin: { horizontal: 10 }
                },
                noData: { text: 'No data available' }
            };
        }
    },

    methods: {
        formatLitres: function (value) {
            if (value == null) return '0 L';
            return Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 }) + ' L';
        },
        formatCurrency: function (value) {
            if (value == null) return 'R 0.00';
            return 'R ' + Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        formatDate: function (value) {
            if (!value) return 'N/A';
            return new Date(value).toLocaleDateString();
        },
        formatDateTime: function (value) {
            if (!value) return 'N/A';
            return new Date(value).toLocaleString();
        },
        formatDateShort: function (value) {
            if (!value) return '';
            return new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
        },
        monthLabel: function (monthNumber) {
            var match = this.monthOptions.filter(function (m) { return m.value === monthNumber; })[0];
            return match ? match.label : '';
        },
        complianceClass: function (status) {
            if (status === 'Active') return 'text-success';
            if (status === 'Stale') return 'text-warning';
            if (status === 'Inactive') return 'text-danger';
            return '';
        },
        // SARSAmountDue from the API is fixed at R3.00/litre server-side. The
        // rate is user-editable here, so recompute it client-side (litres x
        // rate, only for eligible equipment) instead of trusting that field.
        computedSarsAmount: function (row) {
            if (!row || row.sarsEligible !== 'Yes') return 0;
            return (Number(row.totalLitresDispensed) || 0) * (Number(this.rebateRatePerLitre) || 0);
        },
        onRebateRateChange: function () {
            try {
                if (window.localStorage && !isNaN(this.rebateRatePerLitre) && this.rebateRatePerLitre >= 0) {
                    localStorage.setItem('sarsRebateRatePerLitre', this.rebateRatePerLitre);
                }
            } catch (e) {
                // localStorage unavailable (private browsing, etc.) - rate still works for this session
            }
        },
        resetFilters: function () {
            var fmt = function (d) { return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); };
            var today = new Date();
            this.filterMode = 'month';
            this.params = {
                year: today.getFullYear(), month: today.getMonth() + 1,
                fromDate: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), toDate: fmt(today)
            };
            this.quickSearch = '';
            this.statusFilter = '';
            this.filterTab = 'all';
            this.masterGroupFilter = '';
            this.costCentreFilter = '';
            this.loadData();
        },
        periodTotals: function (rows) {
            var self = this;
            var totalLitres = 0, totalSars = 0, eligibleCount = 0, nonEligibleCount = 0, totalTransactions = 0;
            rows.forEach(function (row) {
                totalLitres += Number(row.totalLitresDispensed) || 0;
                totalSars += self.computedSarsAmount(row);
                totalTransactions += Number(row.transactionCount) || 0;
                if (row.sarsEligible === 'Yes') eligibleCount++; else nonEligibleCount++;
            });
            return {
                totalLitres: totalLitres, totalSars: totalSars, eligibleCount: eligibleCount,
                nonEligibleCount: nonEligibleCount, totalTransactions: totalTransactions, equipmentCount: rows.length
            };
        },
        selectFilterTab: function (tab) {
            this.filterTab = tab;
            this.masterGroupFilter = '';
            this.costCentreFilter = '';
            this.expandedUsageGroup = null;
        },
        toggleUsageGroup: function (name) {
            this.expandedUsageGroup = this.expandedUsageGroup === name ? null : name;
        },
        openTotalsBreakdown: function (type) {
            this.totalsBreakdownOpen = type;
        },
        closeTotalsBreakdown: function () {
            this.totalsBreakdownOpen = null;
        },
        // Lets the user pick which metrics plot on the trend chart. Always
        // keeps at least one selected - an empty chart isn't a useful
        // state to toggle into.
        toggleTrendMetric: function (key) {
            var idx = this.selectedTrendMetrics.indexOf(key);
            if (idx !== -1) {
                if (this.selectedTrendMetrics.length === 1) return;
                this.selectedTrendMetrics.splice(idx, 1);
            } else {
                this.selectedTrendMetrics.push(key);
            }
        },
        exportToExcel: function () {
            var self = this;
            var data = self.filteredRows.map(function (r) {
                return {
                    Equipment: r.equipment,
                    Registration: r.equipmentRegistration,
                    'Master Group': r.masterEquipmentGroup,
                    'Cost Centre': r.costCentre,
                    Eligible: r.sarsEligible === 'Yes' ? 'Eligible' : 'Non-Eligible',
                    'Litres Dispensed': Number(r.totalLitresDispensed) || 0,
                    'SARS Amount': self.computedSarsAmount(r),
                    Transactions: r.transactionCount,
                    Status: r.complianceStatus,
                    'Last Activity': self.formatDate(r.lastDispensingDate)
                };
            });
            var ws = XLSX.utils.json_to_sheet(data);
            var wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'SARS Tracker');
            XLSX.writeFile(wb, 'SARS_Tracker_' + new Date().toISOString().slice(0, 10) + '.xlsx');
        },
        buildQueryString: function () {
            if (this.filterMode === 'range') {
                if (!this.params.fromDate || !this.params.toDate) return null;
                return 'startDate=' + encodeURIComponent(this.params.fromDate) + '&endDate=' + encodeURIComponent(this.params.toDate);
            }
            if (this.filterMode === 'year') {
                return 'startDate=' + this.params.year + '-01-01&endDate=' + this.params.year + '-12-31';
            }
            return 'year=' + this.params.year + '&month=' + this.params.month;
        },
        // Same-length period immediately preceding the current selection -
        // "last month" when filterMode is month, the prior equal-length
        // range when filterMode is range, and last year when filterMode is
        // year - for the "This Period vs Previous" comparison.
        buildPreviousQueryString: function () {
            var fmt = function (d) { return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); };

            if (this.filterMode === 'year') {
                var prevYear = this.params.year - 1;
                return 'startDate=' + prevYear + '-01-01&endDate=' + prevYear + '-12-31';
            }
            if (this.filterMode === 'range') {
                if (!this.params.fromDate || !this.params.toDate) return null;
                var from = new Date(this.params.fromDate);
                var to = new Date(this.params.toDate);
                var days = Math.round((to - from) / 86400000) + 1;
                var prevTo = new Date(from);
                prevTo.setDate(prevTo.getDate() - 1);
                var prevFrom = new Date(prevTo);
                prevFrom.setDate(prevFrom.getDate() - (days - 1));
                return 'startDate=' + fmt(prevFrom) + '&endDate=' + fmt(prevTo);
            }
            // month mode
            var year = this.params.year, month = this.params.month - 1;
            if (month < 1) { month = 12; year -= 1; }
            return 'year=' + year + '&month=' + month;
        },
        loadData: function () {
            var self = this;
            var qs = self.buildQueryString();
            if (qs === null) { alert('Please select both a From Date and To Date.'); return; }
            self.loading = true;
            self.rows = [];
            self.previousRows = [];
            self.previousLoaded = false;

            var onSuccess = function (response) { if (response.data) self.rows = response.data; };
            self.$ajaxGet(self, 'SARSEquipmentTracking?' + qs, onSuccess, function () { self.loading = false; });

            var prevQs = self.buildPreviousQueryString();
            if (prevQs !== null) {
                var onPrevSuccess = function (response) { if (response.data) self.previousRows = response.data; };
                self.$ajaxGet(self, 'SARSEquipmentTracking?' + prevQs, onPrevSuccess, function () { self.previousLoaded = true; });
            }

            self.loadTrendData();
        },
        // Headroom-padded max for one trend series' y-axis - see the note
        // above periodComparisonChartOptions for why the two axes need
        // different padding multipliers.
        trendAxisMax: function (field, headroom) {
            var max = this.trendData.reduce(function (m, t) { return Math.max(m, Number(t[field]) || 0); }, 0);
            return max > 0 ? max * headroom : 1;
        },
        // Last 6 calendar months up to and including the current one, each
        // fetched and reduced to the same totals used everywhere else on
        // this page - feeds the multi-point trend line instead of the old
        // two-dot "previous vs now" chart.
        loadTrendData: function () {
            var self = this;
            var monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            var today = new Date();
            var periods = [];
            for (var i = 5; i >= 0; i--) {
                var d = new Date(today.getFullYear(), today.getMonth() - i, 1);
                periods.push({ year: d.getFullYear(), month: d.getMonth() + 1, label: monthNames[d.getMonth()] + ' ' + d.getFullYear() });
            }

            self.trendLoading = true;
            self.trendData = periods.map(function (p) {
                return { label: p.label, totalLitres: 0, totalSars: 0, totalTransactions: 0 };
            });

            var pending = periods.length;
            periods.forEach(function (p, idx) {
                var onSuccess = function (response) {
                    if (response.data) {
                        self.$set(self.trendData, idx, Object.assign({ label: p.label }, self.periodTotals(response.data)));
                    }
                };
                var onFinally = function () {
                    pending--;
                    if (pending === 0) self.trendLoading = false;
                };
                self.$ajaxGet(self, 'SARSEquipmentTracking?year=' + p.year + '&month=' + p.month, onSuccess, onFinally);
            });
        },
        openDrillDown: function (row) {
            this.selectedEquipment = row;
            this.transactionDetail = null;
        },
        closeDrillDown: function () {
            this.selectedEquipment = null;
            this.transactionDetail = null;
        },
        viewFullTransaction: function () {
            var self = this;
            if (!self.selectedEquipment || !self.selectedEquipment.lastUsageDispensingID) return;
            self.transactionLoading = true;
            self.transactionDetail = null;
            var url = 'SARSEquipmentTracking/TransactionDetail?usageDispensingId=' + self.selectedEquipment.lastUsageDispensingID;
            var onSuccess = function (response) { if (response.data) self.transactionDetail = response.data; };
            self.$ajaxGet(self, url, onSuccess, function () { self.transactionLoading = false; });
        },
        // Shortcut for the eye-icon button on a card: opens the drill-down
        // and immediately loads its full transaction, instead of making the
        // user open the drill-down first and then press the button inside.
        viewFullTransactionForRow: function (row) {
            this.openDrillDown(row);
            this.viewFullTransaction();
        },
        // Scoped to the currently-selected period filter, matching the
        // "Transactions" count shown on the equipment card - not the
        // equipment's full lifetime history.
        openTransactionList: function (row) {
            var self = this;
            self.transactionListEquipment = row;
            self.transactionList = [];
            self.transactionListLoading = true;
            var qs = self.buildQueryString();
            var url = 'SARSEquipmentTracking/TransactionList?equipmentId=' + row.equipmentID + (qs ? '&' + qs : '');
            var onSuccess = function (response) { if (response.data) self.transactionList = response.data; };
            self.$ajaxGet(self, url, onSuccess, function () { self.transactionListLoading = false; });
        },
        closeTransactionList: function () {
            this.transactionListEquipment = null;
            this.transactionList = [];
        },
        // Keeps the rate in sync if it's changed from another tab (e.g. a
        // second Tracker window, or the Dashboard) - see the matching note
        // on SARSDashboard.vue for why this matters for correctness.
        onRebateRateStorageChange: function (e) {
            if (e.key !== 'sarsRebateRatePerLitre') return;
            var v = parseFloat(e.newValue);
            if (!isNaN(v) && v >= 0) this.rebateRatePerLitre = v;
        },
        // Closes the resources popover on an outside click.
        onDocumentClick: function (e) {
            if (this.resourcesOpen && !e.target.closest('.resources-badge')) this.resourcesOpen = false;
        }
    },

    mounted: function () {
        var fmt = function (d) { return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); };
        var today = new Date();
        this.params.fromDate = fmt(new Date(today.getFullYear(), today.getMonth(), 1));
        this.params.toDate = fmt(today);
        this.loadData();
        window.addEventListener('storage', this.onRebateRateStorageChange);
        document.addEventListener('click', this.onDocumentClick);
    },

    beforeDestroy: function () {
        window.removeEventListener('storage', this.onRebateRateStorageChange);
        document.removeEventListener('click', this.onDocumentClick);
    }
};
</script>

<style scoped>
.header-toolbar {
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    padding: 16px 0;
    box-shadow: 0 4px 16px rgba(0, 0, 0, .15);
    flex-shrink: 0;
    position: relative;
    z-index: 45;
}
.popover-backdrop { position: fixed; inset: 0; background: rgba(0, 0, 0, .45); z-index: 40; }
.header-content { display: flex; justify-content: space-between; align-items: center; width: 100%; padding: 0 20px; }
.logo-section { display: flex; align-items: center; gap: 12px; }
.logo-icon { width: 40px; height: 40px; background: white; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 1.2rem; }
.dashboard-title { color: white; font-size: 1.5rem; font-weight: 600; }
.header-controls { display: flex; align-items: center; gap: 20px; }
.live-indicator { display: flex; align-items: center; gap: 8px; background: rgba(255, 255, 255, .2); padding: 8px 16px; border-radius: 20px; }

.resources-badge {
    position: relative; display: flex; align-items: center; gap: 8px; cursor: pointer;
    background: rgba(255, 255, 255, .2); color: #fff; padding: 8px 16px; border-radius: 20px;
    font-size: .875rem; font-weight: 500;
}
.date-range-chevron { transition: transform .2s; font-size: .7rem; }
.date-range-chevron.open { transform: rotate(180deg); }
.resources-popover {
    position: absolute; top: calc(100% + 10px); right: 0; background: #fff; border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, .2); padding: 8px; min-width: 260px; z-index: 50; cursor: default;
}
.resources-link {
    display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-radius: 8px;
    color: #334155; font-size: .85rem; font-weight: 500; text-decoration: none; transition: background .15s;
}
.resources-link:hover { background: #fff9f5; color: #D2691E; }
.live-dot { width: 8px; height: 8px; background: #4CAF50; border-radius: 50%; animation: pulse 2s infinite; }
@keyframes pulse {
    0% { box-shadow: 0 0 0 0 rgba(76, 175, 80, .7); }
    70% { box-shadow: 0 0 0 10px rgba(76, 175, 80, 0); }
    100% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0); }
}

.main-content { flex: 1; overflow-y: auto; padding: 20px; background: #e2e8f0; }
.main-content > * { max-width: 1600px; margin-left: auto; margin-right: auto; }

.search-section { background: #fff; border-radius: 12px; margin-bottom: 20px; box-shadow: 0 2px 4px rgba(0, 0, 0, .15); border: 1px solid #cbd5e1; }
.filter-header { padding: 16px 20px; background: #f1f5f9; border-bottom: 1px solid #cbd5e1; border-radius: 12px 12px 0 0; display: flex; justify-content: space-between; align-items: center; }
.filter-title { font-size: 1.125rem; font-weight: 600; color: #34495E; margin: 0; }
.filter-header-buttons { display: flex; gap: 10px; align-items: center; }
.reset-filter-header-btn {
    padding: 8px 16px; background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); border: none; color: white;
    font-size: .9rem; font-weight: 500; border-radius: 8px; cursor: pointer; transition: all .3s;
    display: flex; align-items: center; gap: 6px;
}
.reset-filter-header-btn:hover { background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%); transform: translateY(-1px); }
.reset-icon { font-size: 1.1rem; font-weight: bold; display: inline-block; transition: transform .3s; }
.reset-filter-header-btn:hover .reset-icon { transform: rotate(180deg); }

.chip-row { display: flex; flex-wrap: wrap; gap: 6px; }
.chip-btn {
    padding: 5px 12px; border: 1px solid #cbd5e1; background: white; border-radius: 16px;
    font-size: .75rem; font-weight: 500; color: #64748b; cursor: pointer; transition: all .2s ease;
}
.chip-btn:hover { border-color: #D2691E; color: #D2691E; background: #fff9f5; }
.chip-btn.active { background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%); color: white; border-color: #D2691E; }
.search-controls-wrapper { padding: 16px 20px; }
.filter-collapse-enter-active, .filter-collapse-leave-active {
    transition: max-height .3s ease, opacity .25s ease, padding .3s ease;
    max-height: 600px; overflow: hidden;
}
.filter-collapse-enter, .filter-collapse-leave-to {
    max-height: 0; opacity: 0; padding-top: 0; padding-bottom: 0;
}
.search-controls { display: flex; flex-wrap: wrap; gap: 14px; align-items: flex-end; }
.search-group { display: flex; flex-direction: column; gap: 2px; flex: 1 1 160px; min-width: 120px; max-width: 220px; }
/* Bare selects/inputs sitting directly in the row (not wrapped in a
   .search-group label+field pair) need their own row-axis sizing. The ">"
   here matters: applying flex-basis to .specific-date-input itself would
   also hit the ones nested a level down inside .search-group, but there
   the parent is a COLUMN flex, so a flex-basis meant as a width gets read
   as a height instead - that's what blew the inputs up to ~170px tall. */
.search-controls > select.specific-date-input,
.search-controls > input.specific-date-input { flex: 1 1 170px; min-width: 120px; max-width: 220px; }

.date-quick-buttons { display: flex; gap: 4px; flex-wrap: wrap; }
.date-quick-btn {
    padding: 5px 10px; border: 1px solid #cbd5e1; background: white; border-radius: 5px;
    font-size: .75rem; font-weight: 500; color: #64748b; cursor: pointer; transition: all .2s ease;
}
.date-quick-btn:hover { border-color: #D2691E; color: #D2691E; background: #fff9f5; }
.date-quick-btn.active { background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%); color: white; border-color: #D2691E; box-shadow: 0 2px 8px rgba(210, 105, 30, .2); }

.specific-date-input {
    padding: 5px 10px; border: 1px solid #cbd5e1; border-radius: 5px; font-size: .85rem;
    color: #334155; background: white; width: 100%;
}
.specific-date-input:focus { outline: none; border-color: #D2691E; box-shadow: 0 0 0 2px rgba(210, 105, 30, .15); }
.specific-date-input-sm { padding: 4px 8px; font-size: .78rem; }

.toggle-filter-btn {
    padding: 8px 16px; background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%); color: white;
    border: none; border-radius: 6px; font-size: .875rem; font-weight: 500; cursor: pointer; transition: all .3s;
}
.toggle-filter-btn:hover { background: linear-gradient(135deg, #FF8C00 0%, #FFA500 100%); transform: translateY(-1px); }
.toggle-filter-btn:disabled { opacity: .6; cursor: not-allowed; }

.loading-overlay-inline { display: flex; justify-content: center; padding: 60px 0; }
.loader { width: 40px; height: 40px; border: 4px solid #f1f5f9; border-top-color: #D2691E; border-radius: 50%; animation: spin .8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

.stats-section { background: #fff; border-radius: 12px; margin-bottom: 20px; box-shadow: 0 2px 4px rgba(0, 0, 0, .15); border: 1px solid #cbd5e1; }
.stats-header { padding: 16px 20px; background: #f1f5f9; border-bottom: 1px solid #cbd5e1; border-radius: 12px 12px 0 0; }
.stats-title { font-size: 1.125rem; font-weight: 600; color: #34495E; margin: 0; }
.stats-subtitle { font-size: .8rem; color: #64748b; margin-top: 2px; }
.stats-wrapper { padding: 16px 20px; }
.stats-container { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 16px; }
.period-comparison-chart { margin-top: 20px; padding-top: 16px; border-top: 1px solid #e2e8f0; }
.trend-metric-picker { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-bottom: 12px; }
.trend-metric-picker-label { font-size: .78rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .03em; margin-right: 4px; }
.trend-metric-picker .chip-btn.active { color: #fff; }
.comparison-summary { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px; margin-top: 16px; }
.comparison-summary-item {
    background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px 14px;
    display: flex; flex-direction: column; gap: 2px;
}
.comparison-summary-label { font-size: .68rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: .04em; }
.comparison-summary-value { font-size: .95rem; font-weight: 700; color: #2c3e50; }
.comparison-summary-change { font-size: .78rem; font-weight: 600; color: #94a3b8; }
.comparison-summary-change.positive { color: #27AE60; }
.comparison-summary-change.negative { color: #E74C3C; }
.stat-card { background: #f8fafc; border-radius: 12px; padding: 16px; box-shadow: 0 1px 3px rgba(0, 0, 0, .08); transition: all .3s; border: 1px solid #e2e8f0; }
.stat-card-clickable { cursor: pointer; }
.stat-card-clickable:hover { border-color: #D2691E; box-shadow: 0 4px 12px rgba(210, 105, 30, .15); }
.stat-card:hover { transform: translateY(-3px); box-shadow: 0 6px 16px rgba(0, 0, 0, .1); background: #fff; }
.total-stat { border-left: 4px solid #FF8C00; }
.average-stat { border-left: 4px solid #E67E22; }
.stat-label { color: #2c3e50; font-size: .875rem; margin-bottom: 8px; font-weight: 600; }
.stat-value { font-size: 1.75rem; color: #2c3e50; font-weight: bold; }
.stat-change { font-size: .8rem; margin-top: 6px; font-weight: 600; color: #94a3b8; }
.stat-change.positive { color: #27AE60; }
.stat-change.negative { color: #E74C3C; }

.card-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 16px; }
.equipment-card {
    background: #f8fafc; border-radius: 12px; padding: 16px; box-shadow: 0 1px 3px rgba(0, 0, 0, .08);
    transition: all .3s; border: 2px solid #FF8C00; position: relative;
}
.card-top { display: flex; justify-content: space-between; align-items: flex-start; gap: 8px; margin-bottom: 4px; }
.card-eye-btn {
    position: absolute; top: 40px; right: 14px; width: 28px; height: 28px; padding: 0;
    background: #fff; color: #D2691E; border: 1px solid #D2691E; border-radius: 50%;
    font-size: 1rem; line-height: 1; cursor: pointer; transition: all .2s;
    display: inline-flex; align-items: center; justify-content: center; z-index: 1;
}
.card-eye-btn:hover { background: #D2691E; color: #fff; transform: scale(1.08); }
.equipment-name {
    font-weight: 700; color: #2c3e50; font-size: 1rem; min-width: 0; flex: 1;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.equipment-badge { flex-shrink: 0; }
.equipment-reg { color: #64748b; font-size: .8rem; margin-bottom: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; padding-right: 32px; }
.card-stats { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin-bottom: 10px; }
.card-stat-label { color: #94a3b8; font-size: .7rem; text-transform: uppercase; font-weight: 600; }
.card-stat-value { color: #2c3e50; font-weight: 700; font-size: .95rem; }
.card-footer { font-size: .75rem; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 8px; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.view-transactions-btn {
    padding: 4px 10px; background: #fff; color: #D2691E; border: 1px solid #D2691E; border-radius: 5px;
    font-size: .7rem; font-weight: 600; cursor: pointer; white-space: nowrap; transition: all .2s;
}
.view-transactions-btn:hover { background: #D2691E; color: #fff; }
.card-footer-actions { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }

.usage-group-list { padding: 10px 20px 16px; }
.usage-group { border-bottom: 1px solid #e2e8f0; }
.usage-group:last-child { border-bottom: none; }
.usage-group-toggle {
    width: 100%; display: flex; align-items: center; gap: 10px; padding: 12px 4px; background: none; border: none;
    cursor: pointer; text-align: left; font-family: inherit;
}
.usage-group-toggle:hover { background: #fff9f5; }
.usage-group-arrow { color: #D2691E; font-size: 1.3rem; font-weight: 700; transition: transform .2s; flex-shrink: 0; }
.usage-group-arrow.open { transform: rotate(90deg); }
.usage-group-name { font-weight: 700; color: #2c3e50; font-size: .9rem; flex: 1; }
.usage-group-meta { color: #64748b; font-size: .78rem; white-space: nowrap; }
.usage-group-body { padding: 0 4px 12px 26px; }

.transaction-list-wrapper { overflow-x: auto; }
.transaction-list-table { width: 100%; border-collapse: collapse; font-size: .85rem; }
.transaction-list-table th {
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%); color: #fff; text-align: left;
    padding: 10px 12px; position: sticky; top: 0;
}
.transaction-list-table td { padding: 8px 12px; border-bottom: 1px solid #e2e8f0; color: #334155; }
.transaction-list-table tr:hover td { background: #fff9f5; }

.text-success { color: #27AE60; }
.text-warning { color: #E67E22; }
.text-danger { color: #E74C3C; }

.badge-eligible { background: #E8F8EF; color: #27AE60; padding: 3px 10px; border-radius: 12px; font-size: .72rem; font-weight: 700; }
.badge-non-eligible { background: #FDECEA; color: #E74C3C; padding: 3px 10px; border-radius: 12px; font-size: .72rem; font-weight: 700; }

.empty-state { text-align: center; padding: 60px 20px; background: #fff; border-radius: 12px; border: 1px solid #cbd5e1; }
.empty-icon { font-size: 2.5rem; margin-bottom: 12px; }
.empty-title { font-size: 1.1rem; font-weight: 700; color: #34495E; margin-bottom: 6px; }
.empty-description { color: #64748b; font-size: .9rem; }

/* Drill-down popup */
.simple-popup {
    position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 9999;
    background: rgba(15, 23, 42, .72); backdrop-filter: blur(3px); -webkit-backdrop-filter: blur(3px);
    display: flex; align-items: center; justify-content: center; padding: 16px; box-sizing: border-box;
}
.popup-content { background: #fff; border-radius: 12px; width: 100%; max-width: 680px; max-height: 80vh; overflow-y: auto; box-shadow: 0 8px 32px rgba(0, 0, 0, .25); }
.popup-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%); border-radius: 12px 12px 0 0; gap: 10px; }
.popup-header h3 { color: white; margin: 0; font-size: 1.1rem; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.close-btn { background: none; border: none; color: white; font-size: 1.5rem; cursor: pointer; line-height: 1; flex-shrink: 0; }
.popup-body { padding: 20px; overflow-x: auto; }
.popup-subtitle { color: #34495E; font-size: .95rem; margin: 16px 0 8px; border-top: 1px solid #e2e8f0; padding-top: 16px; }
.detail-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 8px 16px; }
.detail-row { font-size: .875rem; color: #334155; }
.transaction-detail { margin-top: 8px; }
.txn-detail-card { background: #f8fafc; border-radius: 10px; border: 1px solid #e2e8f0; overflow: hidden; }
.txn-detail-row { padding: 12px 16px; background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%); display: flex; flex-direction: column; gap: 2px; }
.txn-detail-highlight .txn-detail-label { color: rgba(255, 255, 255, .85); }
.txn-detail-highlight .txn-detail-value { color: #fff; font-size: 1.05rem; }
.txn-detail-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 12px 16px; padding: 14px 16px; }
.txn-detail-cell { display: flex; flex-direction: column; gap: 2px; }
.txn-detail-label { font-size: .68rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: .04em; }
.txn-detail-value { font-size: .9rem; font-weight: 600; color: #2c3e50; }
.txn-detail-description { padding: 0 16px 14px; display: flex; flex-direction: column; gap: 2px; }

/* Small screens - stack the header, tighten spacing, and let filter/summary
   rows drop to one column instead of squeezing everything into place. */
@media (max-width: 640px) {
    .header-content { flex-direction: column; align-items: flex-start; gap: 10px; }
    .main-content { padding: 12px; }
    .stats-container, .kpi-grid, .card-grid, .charts-section, .comparison-summary { grid-template-columns: 1fr; }
    .search-controls { flex-direction: column; align-items: stretch; }
    .search-group { max-width: none; }
    .popup-content { width: 96%; }
    .filter-header, .stats-header { flex-wrap: wrap; gap: 10px; }
    .filter-header-buttons { flex-wrap: wrap; }
}
</style>
